import { Injectable, inject } from '@angular/core';
import { fhirR4 } from '@smile-cdr/fhirts';
import cloneDeep from 'lodash/cloneDeep';
import { AppContext } from 'src/app/models/AppContext';
import { ResourceType } from '../models/ResourceType';
import { DataService } from '../services/data.service';
import { ExtensionService } from '../services/extension.service';
import { ContextService } from './context.service';

@Injectable({
  providedIn: 'root',
})
export class QrBuilderService {
  private readonly contextService = inject(ContextService);
  private readonly dataService = inject(DataService);
  private readonly extensionService = inject(ExtensionService);

  readonly contextExtensionUrl =
    'http://hl7.org/fhir/us/davinci-dtr/StructureDefinition/qr-context'; // NOSONAR: FHIR canonical URI, not a network endpoint
  readonly toRemoveExtensionUrl =
    'http://hl7.org/fhir/us/davinci-dtr/StructureDefinition/dtr-questionnaireresponse-questionnaire'; // NOSONAR: FHIR canonical URI, not a network endpoint
  readonly intendedUseUrl =
    'http://hl7.org/fhir/us/davinci-dtr/StructureDefinition/intendedUse'; // NOSONAR: FHIR canonical URI, not a network endpoint
  readonly intendedUseCoding =
    'http://hl7.org/fhir/us/davinci-crd/CodeSystem/temp'; // NOSONAR: FHIR canonical URI, not a network endpoint

  async createDtrQr(
    qr: fhirR4.QuestionnaireResponse,
    questionnaire: fhirR4.Questionnaire,
    appContext: AppContext,
  ) {
    const qrCopy = this.addContainedQuestionnaire(cloneDeep(qr), questionnaire);
    this.extensionService.addAutoOrigin(qrCopy.item);
    const dtrQr = this.addContextExtension(qrCopy, appContext);
    return await this.dataService.createResource(dtrQr);
  }

  addContainedQuestionnaire(
    qr: fhirR4.QuestionnaireResponse,
    questionnaire: fhirR4.Questionnaire,
  ) {
    qr.questionnaire = '#' + questionnaire.id;
    if (qr['contained']) {
      const index = qr['contained'].findIndex(
        (element) => element.resourceType === ResourceType.Questionnaire,
      );
      if (index >= 0) qr['contained'][index] = questionnaire;
      else qr['contained'].push(questionnaire);
    } else {
      qr['contained'] = [questionnaire];
    }
    return qr;
  }

  addContextExtension(
    qr: fhirR4.QuestionnaireResponse,
    appContext: AppContext,
  ): fhirR4.QuestionnaireResponse {
    qr.extension = (qr.extension ?? [])
      .filter((ext) => ext.url !== this.toRemoveExtensionUrl)
      .concat([
        {
          url: this.contextExtensionUrl,
          valueReference: {
            reference: this.contextService.getCoverageId(appContext),
          },
        },
        {
          url: this.contextExtensionUrl,
          valueReference: {
            reference: this.contextService.getOrderReference(appContext),
          },
        },
        {
          url: this.intendedUseUrl,
          valueCodeableConcept: {
            coding: [
              {
                system: this.intendedUseCoding,
                code: 'withpa',
                display: 'Include in prior authorization',
              },
            ],
          },
        },
      ]);
    return qr;
  }

  async addQRReferenceToTask(
    task: fhirR4.Task,
    qr: fhirR4.QuestionnaireResponse,
  ) {
    task.status = 'in-progress';
    const qrOutput = {
      type: {
        coding: [
          {
            system: 'http://hl7.org/fhir/uv/sdc/CodeSystem/temp', // NOSONAR: FHIR canonical URI, not a network endpoint
            code: 'questionnaire-response',
          },
        ],
      },
      valueReference: {
        reference: qr.resourceType + '/' + qr.id,
      },
    };
    if (task['output']) {
      const qrIndex = task.output.findIndex((output) =>
        output.type.coding?.find(
          (coding) => coding.code === 'questionnaire-response',
        ),
      );
      if (qrIndex >= 0) {
        task.output[qrIndex] = qrOutput;
      } else {
        task.output.push(qrOutput);
      }
    } else {
      task.output = [qrOutput];
    }
    await this.dataService.updateResource(task);
  }

  async addCoverageExtensionToOrder(
    appContext: AppContext,
    qr: fhirR4.QuestionnaireResponse,
  ) {
    const newExt = {
      url: 'response',
      valueReference: {
        reference: qr.resourceType + '/' + qr.id,
      },
    };
    const order = await this.contextService.getOrderResource(appContext);
    if (order['extension']) {
      const coverageExt = ExtensionService.getPAExtension(order);
      const responseIndex = coverageExt.extension.findIndex(
        (ext) => ext.url === newExt.url,
      );
      if (responseIndex >= 0) {
        coverageExt.extension[responseIndex] = newExt;
      } else {
        coverageExt.extension.push(newExt);
      }
    } else {
      order['extension'] = [
        {
          url: ExtensionService.coverageExtensionUrl,
          extension: [newExt],
        },
      ];
    }
    await this.dataService.updateResource(order);
  }
}
